import React, { useCallback, useEffect, useRef, useState } from 'react';
import { chapterCount } from './chapterSequence';
import { fractionAt, anchoredFraction, pageAt, pageFraction, edgeAt, crossBook, keyStep } from './scrub';
import { bookName } from './ChapterPreview';

// Hold the pill this long to start scrubbing with the same finger.
const HOLD_MS = 300;
// Hold at either end of the track this long to carry on into the next book.
const EDGE_HOLD_MS = 600;
// A finger that moves this far before the hold fires is scrolling the page.
const MOVE_CANCEL_PX = 8;
// After the last arrow key on the focused slider, go there.
const KEY_COMMIT_MS = 700;

// ── Fast travel through a book ──────────────────────────────────────────────
// A slim bar pinned to the bottom of the chapter. It names where you are
// ("Genesis 12 / 50") and holds a track one book wide, a segment per page:
// the book's outline page first ("Genesis · Outline", ch null), then each
// chapter. It is on the outline page too, where `chapter` is null.
//   Mouse: the track is always there; press or drag on it, and let go to go.
//   Touch: only the pill shows, centred at the bottom, until it is held
//          (~300 ms). The track then opens above it and the same finger
//          slides left or right from where it is, with a bubble naming the
//          chapter; lifting goes there. Sliding starts from the current
//          chapter and the track's ends are chapter 1 and the last, so
//          nothing jumps and both ends are in reach. A short tap opens the
//          track for tapping or dragging; tapping elsewhere puts it away.
//   Either: hold at an end for ~600 ms and the track moves on to the
//          neighbouring book, again every 600 ms while held (Genesis outline
//          ← Exodus, Exodus 40 → Leviticus).
//   Keyboard: the track is a slider; arrows, PageUp/PageDown, Home/End pick,
//          Enter (or a pause) goes, Escape cancels.
// `onJump({ book, ch })` turns to the choice; `onPreview` is told what is
// under the finger so the page can fetch it before it is needed.
export function ChapterScrubber({ book, chapter, lang, t, onJump, onPreview }) {
  const [open, setOpen] = useState(false);
  const [scrub, setScrub] = useState(null);        // { book, ch } while choosing
  const scrubRef = useRef(null);
  scrubRef.current = scrub;
  const trackRef = useRef(null);
  const rootRef = useRef(null);
  const st = useRef({ mode: null, pointerId: null, x0: 0, y0: 0, f0: 0.5, edge: 0, holdTimer: null, edgeTimer: null, keyTimer: null });

  const shown = scrub || { book, ch: chapter };
  const count = chapterCount(shown.book);
  const f = pageFraction(shown.ch, count);
  const pageLabel = p => (p.ch ? t.chapterLabel(bookName(p.book, lang), p.ch) : t.outlinePage(bookName(p.book, lang)));
  const label = pageLabel(shown);

  const clearTimers = () => {
    const s = st.current;
    clearTimeout(s.holdTimer); clearTimeout(s.edgeTimer); clearTimeout(s.keyTimer);
    s.holdTimer = s.edgeTimer = s.keyTimer = null;
  };
  useEffect(() => () => clearTimers(), []);

  // A new chapter from elsewhere ends any choosing in progress.
  useEffect(() => { setScrub(null); }, [book, chapter]);

  // Tell the page what is under the finger, a moment after it settles.
  useEffect(() => {
    if (!scrub || !onPreview) return;
    const id = setTimeout(() => onPreview(scrub), 180);
    return () => clearTimeout(id);
  }, [scrub, onPreview]);

  // Tapping anywhere else puts an opened track away.
  useEffect(() => {
    if (!open) return;
    const away = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setScrub(null); }
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const finish = useCallback((go) => {
    const s = st.current;
    const choice = scrubRef.current;
    clearTimers();
    s.mode = null; s.pointerId = null; s.edge = 0;
    setScrub(null);
    setOpen(false);
    if (go && choice && (choice.book !== book || choice.ch !== chapter)) onJump(choice);
  }, [book, chapter, onJump]);

  // Holding at an end: move to the neighbouring book, and again while held.
  const scheduleEdge = (edge, frac) => {
    const s = st.current;
    clearTimeout(s.edgeTimer);
    s.edgeTimer = setTimeout(() => {
      const cur = scrubRef.current;
      if (!cur || st.current.edge !== edge) return;
      const next = crossBook(cur.book, edge, frac);
      if (!next) return;
      setScrub(next);
      scheduleEdge(edge, frac);
    }, EDGE_HOLD_MS);
  };

  // The finger (or mouse) is at clientX: choose the chapter under it. Pressed
  // on the track, that is the chapter right there; slid from a held pill, it
  // is measured from where the finger started (anchoredFraction).
  const follow = (x) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    const s0 = st.current;
    const frac = s0.mode === 'hold'
      ? anchoredFraction(x, s0.x0, s0.f0, r.left, r.width)
      : fractionAt(x, r.left, r.width);
    const cur = scrubRef.current || { book, ch: chapter };
    setScrub({ book: cur.book, ch: pageAt(frac, chapterCount(cur.book)) });
    const s = st.current;
    const edge = edgeAt(x, r.left, r.width);
    if (edge !== s.edge) {
      s.edge = edge;
      clearTimeout(s.edgeTimer);
      if (edge) scheduleEdge(edge, frac);
    }
  };

  // ── The pill (touch: hold to scrub, tap to open) ─────────────────────────
  const onPillDown = e => {
    if (e.pointerType === 'mouse') return;     // the track is always shown to a mouse
    const s = st.current;
    s.mode = 'pending'; s.pointerId = e.pointerId; s.x0 = e.clientX; s.y0 = e.clientY;
    const target = e.currentTarget;
    clearTimeout(s.holdTimer);
    s.holdTimer = setTimeout(() => {
      s.holdTimer = null;
      if (s.mode !== 'pending') return;
      s.mode = 'hold';
      s.f0 = pageFraction(chapter, chapterCount(book));
      try { target.setPointerCapture(s.pointerId); } catch (_) { /* pointer already gone */ }
      setOpen(true);
      setScrub({ book, ch: chapter });
      navigator.vibrate?.(8);
    }, HOLD_MS);
  };
  const onPillMove = e => {
    const s = st.current;
    if (e.pointerId !== s.pointerId) return;
    if (s.mode === 'pending') {
      if (Math.hypot(e.clientX - s.x0, e.clientY - s.y0) > MOVE_CANCEL_PX) { clearTimers(); s.mode = null; }
      return;
    }
    if (s.mode === 'hold') follow(e.clientX);
  };
  const onPillUp = e => {
    const s = st.current;
    if (e.pointerId !== s.pointerId) return;
    if (s.mode === 'hold') { finish(true); return; }
    if (s.mode === 'pending') {          // a tap: open (or close) the track
      clearTimers(); s.mode = null;
      setOpen(o => !o);
      setScrub(null);
    }
  };
  const onPillCancel = () => { if (st.current.mode) finish(false); };
  const onPillClick = e => {
    // Mouse and keyboard: put the keyboard on the slider. A keyboard or
    // screen-reader activation (detail 0) must open it first: on touch screens
    // the track is hidden until open, and focusing a hidden track does nothing.
    if (e.detail === 0) setOpen(true);
    if (e.detail === 0 || e.nativeEvent?.pointerType === 'mouse') {
      requestAnimationFrame(() => trackRef.current?.focus());
    }
  };

  // ── The track (press or drag directly) ───────────────────────────────────
  const onTrackDown = e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const s = st.current;
    s.mode = 'drag'; s.pointerId = e.pointerId;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* fine */ }
    e.preventDefault();          // no text selection, and keep focus behaviour ours
    trackRef.current?.focus({ preventScroll: true });
    follow(e.clientX);
  };
  const onTrackMove = e => {
    const s = st.current;
    if (s.mode !== 'drag' || e.pointerId !== s.pointerId) return;
    follow(e.clientX);
  };
  const onTrackUp = e => {
    const s = st.current;
    if (s.mode !== 'drag' || e.pointerId !== s.pointerId) return;
    finish(true);
  };
  const onTrackCancel = () => { if (st.current.mode === 'drag') finish(false); };

  const onKeyDown = e => {
    if (e.key === 'Escape') { if (scrubRef.current) { e.preventDefault(); clearTimers(); setScrub(null); } return; }
    if (e.key === 'Enter') { if (scrubRef.current) { e.preventDefault(); finish(true); } return; }
    const cur = scrubRef.current || { book, ch: chapter };
    // Position 0 is the outline page.
    const pos = keyStep(e.key, cur.ch || 0, chapterCount(cur.book), 0);
    if (pos == null) return;
    e.preventDefault();
    setScrub({ book: cur.book, ch: pos || null });
    const s = st.current;
    clearTimeout(s.keyTimer);
    s.keyTimer = setTimeout(() => finish(true), KEY_COMMIT_MS);
  };
  const onBlur = () => { if (st.current.keyTimer) finish(true); };

  const bubbleLeft = Math.min(92, Math.max(8, f * 100));
  const active = open || !!scrub;

  return (
    <div ref={rootRef} className={`chapter-scrub${active ? ' open' : ''}`}>
      <div className="chapter-scrub-inner">
        <button type="button" className="chapter-scrub-pill"
          onPointerDown={onPillDown} onPointerMove={onPillMove} onPointerUp={onPillUp}
          onPointerCancel={onPillCancel} onClick={onPillClick} onContextMenu={e => e.preventDefault()}
          aria-label={t.scrubOpen} title={t.scrubHint}>
          <span className="chapter-scrub-name">{bookName(book, lang)}</span>{' '}
          {chapter ? (
            <>
              <span className="chapter-scrub-num">{chapter}</span>
              <span className="chapter-scrub-count"> / {chapterCount(book)}</span>
            </>
          ) : (
            <span className="chapter-scrub-num">· {t.outlineShort}</span>
          )}
        </button>
        <div ref={trackRef} className="chapter-scrub-track" role="slider" tabIndex={0}
          aria-label={t.scrubLabel} aria-valuemin={0} aria-valuemax={count}
          aria-valuenow={shown.ch || 0} aria-valuetext={label}
          onPointerDown={onTrackDown} onPointerMove={onTrackMove} onPointerUp={onTrackUp}
          onPointerCancel={onTrackCancel} onKeyDown={onKeyDown} onBlur={onBlur}>
          <div className="chapter-scrub-rail">
            <div className="chapter-scrub-fill" style={{ width: `${f * 100}%` }} />
          </div>
          <div className="chapter-scrub-thumb" style={{ left: `${f * 100}%` }} />
          {scrub && (
            <div className="chapter-scrub-bubble" style={{ left: `${bubbleLeft}%` }} aria-hidden="true">
              {label}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
